import type {
  QuotedPrice,
  SavedPaymentMethodPaymentResponse,
} from "@pax/api-contract/payments/v1alpha1";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@pax/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import { type CreditOffer, creditsOf, formatMoney } from "../format";
import { CHECKOUT_CLOSE_PATH, openStripeTab, sendToStripe } from "../resume-after-checkout";
import { CancellationWaiver } from "./CancellationWaiver";

const creditCount = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** How long a purchase Stripe is still finishing is asked about again, and how often. */
const PENDING_WAIT_MS = 30_000;
const PENDING_POLL_MS = 2_000;

type Stage =
  | { name: "confirm"; notice: "price_changed" | "price_unconfirmed" | null }
  | { name: "stripe"; reason: "bank_check" | "address"; url?: string }
  | { name: "declined" }
  | { name: "pending" }
  | { name: "processing" };

/**
 * Asks the player to confirm a saved-payment-method purchase at its price with tax, then pays. The
 * method is charged the total shown, or nothing.
 */
export function ConfirmPurchaseDialog({
  offer,
  paymentMethodLabel,
  cancellationWaiverRequired,
  onClose,
  onPaid,
  onUseDifferentPaymentMethod,
}: {
  offer: CreditOffer | null;
  paymentMethodLabel: string;
  /** The player must tick the cancellation waiver before they pay. */
  cancellationWaiverRequired: boolean;
  onClose: () => void;
  /** The purchase went through, or Stripe is still finishing it. */
  onPaid: () => void;
  /** Opens Checkout for the same offer. Called inside the click, so its tab is allowed. */
  onUseDifferentPaymentMethod: () => void;
}) {
  return (
    <Dialog open={offer !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent nestedBackdrop>
        {offer && (
          <ConfirmPurchase
            key={offer.offerId}
            offer={offer}
            paymentMethodLabel={paymentMethodLabel}
            cancellationWaiverRequired={cancellationWaiverRequired}
            onClose={onClose}
            onPaid={onPaid}
            onUseDifferentPaymentMethod={onUseDifferentPaymentMethod}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmPurchase({
  offer,
  paymentMethodLabel,
  cancellationWaiverRequired,
  onClose,
  onPaid,
  onUseDifferentPaymentMethod,
}: {
  offer: CreditOffer;
  paymentMethodLabel: string;
  cancellationWaiverRequired: boolean;
  onClose: () => void;
  onPaid: () => void;
  onUseDifferentPaymentMethod: () => void;
}) {
  const [purchaseAttemptId, setPurchaseAttemptId] = useState(() => crypto.randomUUID());
  const [stage, setStage] = useState<Stage>({ name: "confirm", notice: null });
  const [newQuote, setNewQuote] = useState<QuotedPrice | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [waived, setWaived] = useState(false);
  const quoteQuery = useQuery({
    ...orpc.payments.savedPaymentMethod.quote.queryOptions({
      input: { body: { offerId: offer.offerId } },
    }),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const quoted = newQuote ?? (quoteQuery.data?.status === "quoted" ? quoteQuery.data : null);
  const needsAddress = quoteQuery.data?.status === "needs_checkout";

  const pay = useMutation({
    mutationFn: async (quote: QuotedPrice): Promise<SavedPaymentMethodPaymentResponse> => {
      const body = {
        offerId: offer.offerId,
        expectedTotalCents: quote.totalCents,
        purchaseAttemptId,
        returnPath: CHECKOUT_CLOSE_PATH,
        ...(cancellationWaiverRequired && { acceptsCancellationWaiver: true as const }),
      };
      const startedAt = Date.now();
      for (;;) {
        const reply = await reachable(api.payments.savedPaymentMethod.pay({ body }));
        if (reply.status !== "pending" || Date.now() - startedAt > PENDING_WAIT_MS) return reply;
        setFinishing(true);
        await new Promise(resolve => setTimeout(resolve, PENDING_POLL_MS));
      }
    },
    onSettled: () => setFinishing(false),
    onSuccess: (reply, quote) => {
      switch (reply.status) {
        case "succeeded":
          onPaid();
          onClose();
          return;
        case "processing":
          onPaid();
          setStage({ name: "processing" });
          return;
        case "pending":
          onPaid();
          setStage({ name: "pending" });
          return;
        case "price_changed":
          setNewQuote(reply.quote);
          setPurchaseAttemptId(crypto.randomUUID());
          setStage({
            name: "confirm",
            notice:
              reply.quote.totalCents === quote.totalCents ? "price_unconfirmed" : "price_changed",
          });
          return;
        case "needs_checkout":
          setStage({ name: "stripe", reason: reply.reason, url: reply.url });
          return;
        case "declined":
          setStage({ name: "declined" });
          return;
      }
    },
  });

  const subject =
    offer.kind === "token_subscription"
      ? `${creditCount.format(creditsOf(offer))} credits a month`
      : offer.name;

  if (stage.name === "pending") {
    return (
      <Outcome
        title="Still processing"
        body="Your credits will appear when it finishes."
        action={<Button onClick={onClose}>Close</Button>}
      />
    );
  }

  if (stage.name === "processing") {
    return (
      <Outcome
        title="Credits added"
        body="Your payment is still clearing. The credits are removed if it fails."
        action={<Button onClick={onClose}>Close</Button>}
      />
    );
  }

  if (stage.name === "declined") {
    return (
      <Outcome
        title="We couldn't complete your payment"
        body={`We couldn't charge ${paymentMethodLabel}. Nothing was charged.`}
        action={
          <Button onClick={onUseDifferentPaymentMethod}>Use a different payment method</Button>
        }
        onCancel={onClose}
      />
    );
  }

  if (stage.name === "stripe" || needsAddress) {
    const reason = stage.name === "stripe" ? stage.reason : "address";
    const url = stage.name === "stripe" ? stage.url : undefined;
    return (
      <Outcome
        title={
          reason === "address"
            ? "Stripe needs your billing address first"
            : "Confirm with your bank"
        }
        body={
          reason === "address"
            ? "Stripe works out the tax from your address. Nothing was charged."
            : "Your bank wants to confirm this purchase on Stripe. Nothing was charged yet."
        }
        action={
          <Button
            onClick={() => {
              if (!url) return onUseDifferentPaymentMethod();
              sendToStripe(openStripeTab(), url);
              onPaid();
              onClose();
            }}
          >
            Continue to Stripe
          </Button>
        }
        onCancel={onClose}
      />
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Confirm your purchase</DialogTitle>
        <DialogDescription>
          {subject}, charged to {paymentMethodLabel}.
          {offer.kind === "token_subscription" && " Renews every month until you cancel."}
        </DialogDescription>
      </DialogHeader>

      {quoted ? (
        <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm tabular-nums">
          <dt className="text-muted-foreground">Price</dt>
          <dd className="text-right">{formatMoney(quoted.subtotalCents)}</dd>
          <dt className="text-muted-foreground">Tax</dt>
          <dd className="text-right">{formatMoney(quoted.taxCents)}</dd>
          <dt className="font-medium">Total</dt>
          <dd className="text-right font-medium">{formatMoney(quoted.totalCents)}</dd>
        </dl>
      ) : quoteQuery.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {requestErrorMessage(quoteQuery.error)}
        </p>
      ) : (
        <Skeleton className="h-16 rounded-lg" />
      )}

      {cancellationWaiverRequired && (
        <CancellationWaiver
          purchase={offer.kind === "pack" ? "pack" : "plan"}
          checked={waived}
          disabled={pay.isPending}
          onCheckedChange={setWaived}
        />
      )}

      {stage.notice && (
        <p role="status" className="text-sm">
          {stage.notice === "price_changed"
            ? "The price changed. Please check the new total."
            : "We could not confirm this price. Nothing was charged. Please try again."}
        </p>
      )}
      {finishing && (
        <p role="status" className="text-sm text-muted-foreground">
          Finishing your purchase…
        </p>
      )}
      {pay.error && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {requestErrorMessage(pay.error)}
        </p>
      )}

      <DialogFooter className="flex-row flex-wrap justify-end">
        <Button variant="outline" disabled={pay.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!quoted || pay.isPending || (cancellationWaiverRequired && !waived)}
          onClick={() => quoted && pay.mutate(quoted)}
        >
          {pay.isPending ? "Paying…" : quoted ? `Pay ${formatMoney(quoted.totalCents)}` : "Pay"}
        </Button>
      </DialogFooter>
    </>
  );
}

function Outcome({
  title,
  body,
  action,
  onCancel,
}: {
  title: string;
  body: string;
  action: ReactNode;
  onCancel?: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{body}</DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex-row flex-wrap justify-end">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {action}
      </DialogFooter>
    </>
  );
}
